import React from 'react';
import { motion } from 'framer-motion';
import { Truck, Clock, Package, CreditCard, Globe, AlertTriangle, MapPin, MailX } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSeo } from '@/lib/seo';

const SECTIONS = [
  {
    icon: Truck,
    title: 'Delivery Options',
    body: [
      { label: 'Standard Delivery', text: 'Estimated delivery within 3–5 working days.' },
      { label: 'Express Delivery', text: 'Estimated delivery within 1–2 working days.' },
      { label: 'Next Day Delivery', text: 'Available for orders placed before 1pm Monday–Thursday. Orders placed on Friday, Saturday or Sunday will be dispatched the following Monday.' },
    ],
  },
  {
    icon: Clock,
    title: 'Dispatch Times',
    body: [
      { text: 'Orders are typically dispatched within 1–2 working days.' },
      { text: 'Custom or made-to-order items may require additional processing time. Estimated dispatch times will be shown on the product page.' },
    ],
  },
  {
    icon: Package,
    title: 'Shipping Partners',
    body: [
      { text: 'We use trusted courier services including Royal Mail, DPD, and UPS to ensure reliable delivery across the UK.' },
    ],
  },
  {
    icon: CreditCard,
    title: 'Delivery Charges',
    body: [
      { text: 'Delivery charges are calculated at checkout based on the delivery method selected and the destination.' },
      { text: 'Free standard delivery may be available on orders over a specified amount (e.g., £100).' },
    ],
  },
  {
    icon: Globe,
    title: 'International Shipping',
    body: [
      { text: 'Currently, we only ship within the UK. For international enquiries, please contact us directly.' },
    ],
  },
  {
    icon: AlertTriangle,
    title: 'Delays and Exceptions',
    body: [
      { text: 'Delivery times are estimates and may be affected by external factors such as weather, strikes, or peak periods.' },
      { text: 'We are not liable for delays caused by courier services, but we will assist in tracking and resolving any issues.' },
    ],
  },
  {
    icon: MapPin,
    title: 'Tracking Your Order',
    body: [
      { text: 'Once your order is dispatched, you will receive a confirmation email with tracking information.' },
    ],
  },
  {
    icon: MailX,
    title: 'Failed Deliveries',
    body: [
      { text: 'If a delivery attempt fails, the courier may leave a card or attempt redelivery. Please follow the instructions provided by the courier.' },
    ],
  },
];

export default function DeliveryInfo() {
  useSeo({
    title: 'Delivery Information — Motion Reality Sim Racing Ltd',
    description: 'Delivery options, dispatch times, shipping partners, charges and tracking for orders from Motion Reality Sim Racing Ltd.',
  });

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="py-16 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-primary text-xs font-bold tracking-widest mb-3">SHIPPING</p>
            <h1 className="text-white font-black text-4xl md:text-5xl uppercase leading-tight mb-4">
              Delivery Information
            </h1>
            <p className="text-white/55 text-sm leading-relaxed max-w-xl">
              At Motion Reality Sim Racing Ltd, we aim to deliver your orders promptly and securely. Please review the following delivery policy to understand how we manage shipping and fulfilment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sections */}
      <section className="px-6 lg:px-10 py-16">
        <div className="max-w-3xl mx-auto space-y-10">
          {SECTIONS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-[#111115] border border-white/5 p-7"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 flex items-center justify-center bg-primary/10 border border-primary/20">
                    <Icon className="w-4 h-4 text-primary" />
                  </div>
                  <h2 className="text-white font-bold text-sm tracking-widest uppercase">{s.title}</h2>
                </div>
                <div className="space-y-3">
                  {s.body.map((item, j) => (
                    <div key={j} className="text-white/55 text-sm leading-relaxed">
                      {item.label && (
                        <span className="text-white font-bold">{item.label}: </span>
                      )}
                      {item.text}
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 lg:px-10 pb-20">
        <div className="max-w-3xl mx-auto bg-[#111115] border border-white/5 p-8 text-center">
          <p className="text-primary text-xs font-bold tracking-widest mb-3">QUESTIONS?</p>
          <h2 className="text-white font-black text-2xl uppercase mb-3">Need Help With Delivery?</h2>
          <p className="text-white/40 text-sm mb-6 max-w-md mx-auto">
            If you have any questions about shipping or your order, our team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/contact" className="bg-primary hover:bg-primary/90 text-white font-bold text-sm px-7 py-3.5 transition-colors">
              CONTACT US
            </Link>
            <Link to="/orders" className="border border-white/15 hover:border-white/30 text-white/70 hover:text-white font-bold text-sm px-7 py-3.5 transition-colors">
              TRACK YOUR ORDER
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}